import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Heading,
  Button,
  ButtonGroup,
  Spinner,
  Flex,
  Text,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Select,
} from "@chakra-ui/react";
import { ChevronRightIcon, ArrowBackIcon } from "@chakra-ui/icons";
import { Line } from "react-chartjs-2";
import Navbar from "../components/Navbar";
import useAxios from "../utils/useAxios";
import DeviceTreeVisualization from "../components/DeviceTreeVisualization";
import GatewayMap from "../components/GatewayMap";

type TrafficData = {
  created_at: string;
  message_count: number;
  unique_devices: number;
  connection_events: number;
  packet_loss: number;
};

type Gateway = {
  gateway_name: string | null;
  gateway_address: string;
  id: number;
  organization_name: string | null;
};

const GatewayDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const api = useAxios();
  const [data, setData] = useState<TrafficData[]>([]);
  const [loading, setLoading] = useState(false);
  const [metric, setMetric] = useState<
    "message_count" | "unique_devices" | "connection_events" | "packet_loss"
  >("message_count");
  const [days, setDays] = useState<number>(7);
  const [gateway, setGateway] = useState<Gateway | null>(null);

  useEffect(() => {
    // Fetch gateway details (we reuse the admin list for now as we don't have a specific single get endpoint known)
    // Optimization: In a real app we should have a GET /gateways/:id endpoint.
    const fetchGatewayInfo = async () => {
      try {
        const res = await api.get("frontend/gateways/admin/");
        const found = res.data.find((g: Gateway) => g.id === Number(id));
        if (found) setGateway(found);
      } catch (e) {
        console.error("Failed to fetch gateway info", e);
      }
    };
    fetchGatewayInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (id) {
      setLoading(true);
      api
        .get(`monitoring/gateway-traffic/${id}/`, { params: { days: days } })
        .then((res) => {
          setData(res.data);
          setLoading(false);
        })
        .catch((err) => {
          console.error(err);
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, days]);

  const cardBg = useColorModeValue("white", "gray.900");

  const chartData = {
    labels: data.map((d) => new Date(d.created_at).toLocaleString()),
    datasets: [
      {
        label:
          metric === "message_count"
            ? "Messages"
            : metric === "unique_devices"
              ? "Unique Devices"
              : metric === "connection_events"
                ? "Connection Events"
                : "Packet Loss",
        data: data.map((d) => d[metric]),
        fill: false,
        borderColor: "rgb(75, 192, 192)",
        tension: 0.1,
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    scales: {
      xAxes: [
        {
          type: "time",
          time: {
            unit: "hour",
          },
        },
      ],
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
          },
        },
      ],
    },
  };

  return (
    <Navbar>
      <Container maxW="8xl" py={6}>
        <Box mb={6}>
          <Button
            leftIcon={<ArrowBackIcon />}
            onClick={() => navigate("/monitoring")}
            variant="ghost"
            mb={2}
          >
            Back to Monitoring
          </Button>
          <Heading size="xl">
            {gateway ? gateway.gateway_name || gateway.gateway_address : `Gateway ${id}`}
          </Heading>
          {gateway?.organization_name && <Text color="gray.500">{gateway.organization_name}</Text>}
        </Box>

        <Box bg={cardBg} p={6} rounded="md" boxShadow="md">
          <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
            <Heading size="md">Traffic History</Heading>
            <Flex gap={4}>
              <ButtonGroup isAttached size="sm">
                {[1, 7, 30].map((d) => (
                  <Button
                    key={d}
                    variant={days === d ? "solid" : "outline"}
                    colorScheme="teal"
                    onClick={() => setDays(d)}
                  >
                    {d} Days
                  </Button>
                ))}
              </ButtonGroup>
              <Select
                w="200px"
                value={metric}
                onChange={(e) => setMetric(e.target.value as any)}
                size="sm"
              >
                <option value="message_count">Messages</option>
                <option value="unique_devices">Unique Devices</option>
                <option value="connection_events">Connection Events</option>
                <option value="packet_loss">Packet Loss</option>
              </Select>
            </Flex>
          </Flex>

          {loading ? (
            <Flex justify="center" align="center" h="400px">
              <Spinner size="xl" />
            </Flex>
          ) : (
            <Box h="500px">
              {data.length > 0 ? (
                <Line data={chartData} options={{ maintainAspectRatio: false }} />
              ) : (
                <Flex justify="center" align="center" h="100%">
                  <Text color="gray.500">No traffic data available</Text>
                </Flex>
              )}
            </Box>
          )}
        </Box>

        {id && <DeviceTreeVisualization gatewayId={id} />}

        {id && <GatewayMap gatewayId={id} />}
      </Container>
    </Navbar>
  );
};

export default GatewayDetailView;
